import type { ConversationResponse, MessageResponse } from '@roko/api/generated/models'

import { privateClient } from '@/data/api/clients/privateClient'
import { streamAnonConversationPrompt, streamAnonConversationPromptResume } from '@/data/api/streamConversationPrompt'

export type TSafetyGateResponse = {
  conversation_id: string
  safety_gate_status: string
  emergency_message?: string | null
}

type TAnonMessageResponse = {
  id: string
  role: string
  content: string
  citations?: Record<string, unknown>[] | null
  created_at: string
}

type TAnonConversationResponse = {
  id: string
  guest_session_id: string
  safety_gate_status?: string
  created_at: string
  messages: TAnonMessageResponse[]
}

const mapAnonMessage = (message: TAnonMessageResponse): MessageResponse => ({
  id: message.id,
  role: message.role,
  content: message.content,
  citations: message.citations ?? null,
  created_at: message.created_at,
})

/** Normalize anon API shape to ConversationResponse used by chat mappers/UI. */
const mapAnonConversation = (
  conversation: TAnonConversationResponse
): ConversationResponse & {
  safety_gate_status?: string
} => ({
  id: conversation.id,
  user_id: null,
  title: 'New chat',
  safety_gate_status: conversation.safety_gate_status,
  created_at: conversation.created_at,
  updated_at: conversation.created_at,
  is_deleted: false,
  deleted_at: null,
  messages: (conversation.messages ?? []).map(mapAnonMessage),
})

export const createAnonConversation = async (): Promise<ConversationResponse> => {
  const { data } = await privateClient.post<TAnonConversationResponse>('/anon/conversations')
  return mapAnonConversation(data)
}

export const getAnonConversation = async (conversationId: string): Promise<ConversationResponse> => {
  const { data } = await privateClient.get<TAnonConversationResponse>(`/anon/conversations/${conversationId}`)
  return mapAnonConversation(data)
}

export type TSendAnonPromptParams = {
  conversationId: string
  content: string
  onChunk?: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
}

/** Stream anon prompt via SSE (same contract as registered /conversations/{id}/prompt). */
export const sendAnonPrompt = async ({
  conversationId,
  content,
  onChunk,
  onEmergency,
  onDangerCheck,
}: TSendAnonPromptParams): Promise<void> => {
  await streamAnonConversationPrompt({
    conversationId,
    content,
    onChunk: onChunk ?? (() => undefined),
    onEmergency,
    onDangerCheck,
  })
}

export type TResumeAnonPromptParams = {
  conversationId: string
  onChunk?: (chunk: string) => void
  onEmergency?: () => void
  onDangerCheck?: () => void
}

export const resumeAnonPrompt = async ({ conversationId, onChunk, onEmergency, onDangerCheck }: TResumeAnonPromptParams): Promise<void> => {
  await streamAnonConversationPromptResume({
    conversationId,
    onChunk: onChunk ?? (() => undefined),
    onEmergency,
    onDangerCheck,
  })
}

export const submitAnonSafetyGate = async (conversationId: string, imminentDanger: boolean): Promise<TSafetyGateResponse> => {
  const { data } = await privateClient.post<TSafetyGateResponse>(`/anon/conversations/${conversationId}/safety-gate`, {
    imminent_danger: imminentDanger,
  })
  return data
}
